import { LitElement, html, css } from 'lit';

/**
 * CrusherFileUpload
 *
 * Form primitive for file selection with drag-drop zone, file-list preview,
 * client-side validation (size, accept-types, multiple), and standard
 * upload-state feedback. Designed for CSV imports (FundedTax brokerage),
 * vendor bulk imports (BankChangeGuard), and any other "user selects file(s)
 * → submit to your endpoint" pattern.
 *
 * Compatible with native form submission AND fetch-based uploads. The
 * component manages the selected-files state + UX only; the actual upload
 * + progress reporting is the consumer's responsibility (fetch with
 * AbortController, XHR with upload progress, FormData submit, etc.).
 *
 * Attributes:
 *   - name           (string) — form field name (default: "file"). Matters when used inside a <form>.
 *   - accept         (string) — MIME / extension whitelist, identical syntax to native input (e.g., ".csv,text/csv" or "image/*").
 *   - multiple       (boolean) — allow multiple files
 *   - max-size       (number) — per-file size cap in bytes; files over the cap surface as validation errors but stay listed
 *   - max-files      (number) — count cap (only relevant when multiple); rejects additions above the cap
 *   - disabled       (boolean) — disables the input + drag-drop
 *   - hint           (string) — helper text under the drop zone (e.g., "CSV files only, max 10 MB")
 *   - label          (string) — primary label inside the drop zone (default: "Drop files here or click to browse")
 *
 * Slots:
 *   - icon  — illustration / icon at the top of the drop zone
 *   - error — overrides built-in error rendering; consumer can render their own error UI
 *
 * Events:
 *   - files-change — { files: File[], errors: { file: File, code: string, message: string }[] }
 *                    Fires when the selected-files set changes (add / remove). `errors` is per-file
 *                    validation issues so the consumer can prevent submission.
 *   - file-remove  — { file: File } when a file is removed via its remove button.
 *
 * Properties (programmatic, in addition to attributes):
 *   - files — read-only File[] of currently-selected files (post-validation).
 *
 * A11y: the drop zone has role="button" + aria-label tied to `label`; the inner
 * <input> stays in the tab order; selected-files list has role="list".
 */
export class CrusherFileUpload extends LitElement {
  static properties = {
    name:     { type: String, reflect: true },
    accept:   { type: String, reflect: true },
    multiple: { type: Boolean, reflect: true },
    maxSize:  { type: Number, attribute: 'max-size', reflect: true },
    maxFiles: { type: Number, attribute: 'max-files', reflect: true },
    disabled: { type: Boolean, reflect: true },
    hint:     { type: String, reflect: true },
    label:    { type: String, reflect: true },
    /* Internal state — not reflected */
    _files:  { state: true },
    _errors: { state: true },
    _dragOver: { state: true }
  };

  constructor() {
    super();
    this.name = 'file';
    this.accept = '';
    this.multiple = false;
    this.maxSize = 0;
    this.maxFiles = 0;
    this.disabled = false;
    this.hint = '';
    this.label = 'Drop files here or click to browse';
    this._files = [];
    this._errors = [];
    this._dragOver = false;
  }

  static styles = css`
    :host {
      display: block;
    }

    .zone {
      position: relative;
      border: 2px dashed var(--crusher-border);
      border-radius: var(--crusher-radius-lg);
      padding: var(--crusher-spacing-6);
      background: var(--crusher-surface);
      color: var(--crusher-fg);
      transition: border-color var(--crusher-motion-duration-fast) var(--crusher-motion-easing-standard),
                  background var(--crusher-motion-duration-fast) var(--crusher-motion-easing-standard);
      text-align: center;
      cursor: pointer;
    }
    .zone:hover:not([data-disabled="true"]) {
      border-color: color-mix(in srgb, var(--crusher-color-brand-primary) 50%, var(--crusher-border));
    }
    .zone[data-drag-over="true"] {
      border-color: var(--crusher-color-brand-primary);
      background: color-mix(in srgb, var(--crusher-color-brand-primary) 4%, var(--crusher-surface));
    }
    .zone[data-disabled="true"] {
      cursor: not-allowed;
      opacity: var(--crusher-opacity-disabled);
    }
    .zone:focus-within {
      outline: var(--crusher-focus-width) solid var(--crusher-color-brand-primary);
      outline-offset: 2px;
    }

    .zone-input {
      /* Native input invisibly fills the drop zone — clicks fall through */
      position: absolute;
      inset: 0;
      opacity: 0;
      cursor: inherit;
    }

    .icon-wrap {
      margin-bottom: var(--crusher-spacing-2);
      color: var(--crusher-fg-muted);
    }
    .icon-default {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 2.5rem;
      height: 2.5rem;
      border-radius: var(--crusher-radius-full);
      background: color-mix(in srgb, var(--crusher-fg) 6%, transparent);
      font-size: 1.25rem;
    }

    .zone-label {
      font-size: var(--crusher-font-size-sm);
      font-weight: var(--crusher-font-weight-medium);
      color: var(--crusher-fg);
      line-height: var(--crusher-line-height-snug);
    }
    .zone-hint {
      margin-top: var(--crusher-spacing-1);
      font-size: var(--crusher-font-size-xs);
      color: var(--crusher-fg-muted);
      line-height: var(--crusher-line-height-snug);
    }

    .files {
      margin-top: var(--crusher-spacing-3);
      display: flex;
      flex-direction: column;
      gap: var(--crusher-spacing-2);
      list-style: none;
      padding: 0;
    }
    .file-item {
      display: flex;
      align-items: center;
      gap: var(--crusher-spacing-3);
      padding: var(--crusher-spacing-2) var(--crusher-spacing-3);
      background: var(--crusher-surface);
      border: var(--crusher-component-border-weight) solid var(--crusher-border);
      border-radius: var(--crusher-radius-md);
      font-size: var(--crusher-font-size-sm);
    }
    .file-item[data-error="true"] {
      border-color: color-mix(in srgb, var(--crusher-feedback-danger) 50%, var(--crusher-border));
      background: color-mix(in srgb, var(--crusher-feedback-danger) 3%, var(--crusher-surface));
    }
    .file-name {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-weight: var(--crusher-font-weight-medium);
    }
    .file-size {
      color: var(--crusher-fg-muted);
      font-size: var(--crusher-font-size-xs);
      font-variant-numeric: tabular-nums;
      flex: none;
    }
    .file-error-msg {
      width: 100%;
      color: var(--crusher-feedback-danger);
      font-size: var(--crusher-font-size-xs);
      margin-top: var(--crusher-spacing-1);
    }
    .file-remove {
      flex: none;
      appearance: none;
      background: transparent;
      border: none;
      color: var(--crusher-fg-muted);
      cursor: pointer;
      padding: var(--crusher-spacing-1);
      border-radius: var(--crusher-radius-sm);
      font: inherit;
      font-size: var(--crusher-font-size-sm);
      line-height: 1;
    }
    .file-remove:hover {
      background: color-mix(in srgb, var(--crusher-fg) 8%, transparent);
      color: var(--crusher-fg);
    }
    .file-remove:focus-visible {
      outline: var(--crusher-focus-width) solid var(--crusher-color-brand-primary);
      outline-offset: 1px;
    }
  `;

  /* ===== Public API ===== */

  get files() { return [...this._files]; }

  /* Clear selected files (consumer call after successful upload). */
  clear() {
    this._files = [];
    this._errors = [];
    const input = this.renderRoot?.querySelector('input[type="file"]');
    if (input) input.value = '';
    this._emitChange();
  }

  /* ===== Helpers ===== */

  _formatSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
  }

  _validateFile(file) {
    if (this.maxSize > 0 && file.size > this.maxSize) {
      return {
        code: 'size',
        message: `Exceeds max size ${this._formatSize(this.maxSize)}.`
      };
    }
    /* Accept-string check is partly handled by the native input itself (file
       picker filters by accept) but drag-drop bypasses that, so we re-check. */
    if (this.accept && this.accept.trim()) {
      const accepted = this.accept.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
      const name = file.name.toLowerCase();
      const type = (file.type || '').toLowerCase();
      const ok = accepted.some((rule) => {
        if (rule.startsWith('.')) return name.endsWith(rule);
        if (rule.endsWith('/*')) return type.startsWith(rule.slice(0, -1));
        return type === rule;
      });
      if (!ok) {
        return {
          code: 'type',
          message: `Not an accepted file type (${this.accept}).`
        };
      }
    }
    return null;
  }

  _handleFiles(fileList) {
    if (this.disabled) return;
    const incoming = Array.from(fileList);
    const next = this.multiple ? [...this._files] : [];
    const errors = [];

    for (const file of incoming) {
      if (this.maxFiles > 0 && next.length >= this.maxFiles) {
        errors.push({ file, code: 'count', message: `Too many files (max ${this.maxFiles}).` });
        continue;
      }
      const validation = this._validateFile(file);
      if (validation) {
        errors.push({ file, ...validation });
      }
      next.push(file);
    }

    this._files = next;
    this._errors = errors;
    this._emitChange();
  }

  _emitChange() {
    this.dispatchEvent(new CustomEvent('files-change', {
      detail: { files: [...this._files], errors: [...this._errors] },
      bubbles: true,
      composed: true
    }));
  }

  _onInputChange(e) {
    this._handleFiles(e.target.files);
  }

  _onDragOver(e) {
    if (this.disabled) return;
    e.preventDefault();
    this._dragOver = true;
  }
  _onDragLeave() {
    this._dragOver = false;
  }
  _onDrop(e) {
    if (this.disabled) return;
    e.preventDefault();
    this._dragOver = false;
    this._handleFiles(e.dataTransfer.files);
  }

  _onZoneKey(e) {
    /* Space / Enter activates the inner input via click() */
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      this.renderRoot.querySelector('input[type="file"]')?.click();
    }
  }

  _removeFile(file) {
    this._files = this._files.filter((f) => f !== file);
    this._errors = this._errors.filter((e) => e.file !== file);
    this.dispatchEvent(new CustomEvent('file-remove', {
      detail: { file },
      bubbles: true,
      composed: true
    }));
    this._emitChange();
  }

  _errorFor(file) {
    return this._errors.find((e) => e.file === file);
  }

  render() {
    const hasErrors = this._errors.length > 0;
    return html`
      <div
        class="zone"
        data-drag-over=${this._dragOver ? 'true' : 'false'}
        data-disabled=${this.disabled ? 'true' : 'false'}
        role="button"
        tabindex=${this.disabled ? -1 : 0}
        aria-label=${this.label}
        aria-disabled=${this.disabled ? 'true' : 'false'}
        @dragover=${this._onDragOver}
        @dragleave=${this._onDragLeave}
        @drop=${this._onDrop}
        @keydown=${this._onZoneKey}>
        <input
          type="file"
          class="zone-input"
          name=${this.name}
          .accept=${this.accept}
          ?multiple=${this.multiple}
          ?disabled=${this.disabled}
          @change=${this._onInputChange}
          aria-hidden="true"
          tabindex="-1" />
        <div class="icon-wrap" aria-hidden="true">
          <slot name="icon"><span class="icon-default">↑</span></slot>
        </div>
        <div class="zone-label">${this.label}</div>
        ${this.hint ? html`<div class="zone-hint">${this.hint}</div>` : null}
      </div>

      ${this._files.length > 0 ? html`
        <ul class="files" role="list">
          ${this._files.map((file) => {
            const err = this._errorFor(file);
            return html`
              <li class="file-item" data-error=${err ? 'true' : 'false'}>
                <span class="file-name" title=${file.name}>${file.name}</span>
                <span class="file-size">${this._formatSize(file.size)}</span>
                <button
                  type="button"
                  class="file-remove"
                  aria-label=${`Remove ${file.name}`}
                  @click=${() => this._removeFile(file)}>×</button>
                ${err ? html`<div class="file-error-msg">${err.message}</div>` : null}
              </li>
            `;
          })}
        </ul>
      ` : null}
      ${hasErrors ? html`<slot name="error"></slot>` : null}
    `;
  }
}

customElements.define('crusher-file-upload', CrusherFileUpload);
